import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'SP BD Group';
const SITE_URL = 'https://spbdgroup.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_aspbd-central/artifacts/fg24f6g6_SP%20BD%20GROUP%20logo.png';

// Spain and Europe focused SEO configuration
export const SEO_CONFIG = {
  home: {
    title: 'SP BD Group | Servicios Empresariales Premium en Espana y Europa',
    titleEn: 'SP BD Group | Premium Business Services in Spain & Europe',
    description: 'SP BD Group ofrece servicios empresariales premium en Espana: restaurantes en Madrid, asesoramiento financiero, servicios legales, importacion y exportacion. Contactenos hoy.',
    descriptionEn: 'SP BD Group offers premium business services in Spain: restaurants in Madrid, financial advisory, legal services, import-export. Contact us today.',
    keywords: 'SP BD Group, servicios empresariales Espana, restaurantes Madrid, asesoria financiera Madrid, abogados Madrid, importacion exportacion Espana, business services Spain, restaurants Madrid',
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "SP BD Group",
      "url": SITE_URL,
      "logo": DEFAULT_IMAGE,
      "description": "Premium multi-business conglomerate serving Spain and Europe",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Calle Mayor",
        "addressLocality": "Madrid",
        "addressRegion": "Madrid",
        "postalCode": "28013",
        "addressCountry": "ES"
      },
      "areaServed": [
        { "@type": "Country", "name": "Spain" },
        { "@type": "Country", "name": "Portugal" },
        { "@type": "Country", "name": "France" },
        { "@type": "Country", "name": "Germany" },
        { "@type": "Country", "name": "Italy" }
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+34-663-091-990",
        "contactType": "customer service",
        "availableLanguage": ["Spanish", "English"]
      },
      "sameAs": []
    }
  },
  restaurants: {
    title: 'Restaurantes Indios en Madrid | Cocina India Premium | SP BD Group',
    titleEn: 'Indian Restaurants in Madrid | Premium Indian Cuisine | SP BD Group',
    description: 'Descubre los mejores restaurantes indios en Madrid. Lucknow Indian Cuisine y Bahari ofrecen autentica cocina india con ingredientes frescos. Reserva tu mesa hoy.',
    descriptionEn: 'Discover the best Indian restaurants in Madrid. Lucknow Indian Cuisine and Bahari offer authentic Indian food with fresh ingredients. Book your table today.',
    keywords: 'restaurantes indios Madrid, comida india Madrid, Lucknow Indian Cuisine, Bahari restaurant, mejores restaurantes indios Espana, Indian restaurants Madrid, best Indian food Spain, authentic Indian cuisine Madrid, restaurante indio Lavapies',
    schema: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Restaurantes Indios de SP BD Group en Madrid",
      "description": "Coleccion de restaurantes indios premium en Madrid, Espana",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Restaurant",
            "name": "Lucknow Indian Cuisine",
            "description": "Restaurante de cocina india autentica en el corazon de Madrid",
            "image": "https://lucknowindiancuisine.com/wp-content/uploads/2026/03/Logo-Lucknow-home.png",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Madrid",
              "addressCountry": "ES"
            },
            "servesCuisine": "Indian",
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "250"
            },
            "url": "https://lucknowindiancuisine.com/"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Restaurant",
            "name": "Bahari Indian Restaurant",
            "description": "Autentica cocina india en Lavapies, Madrid",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Lavapies",
              "addressLocality": "Madrid",
              "addressCountry": "ES"
            },
            "servesCuisine": "Indian",
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "180"
            },
            "url": "https://bahariindianrestaurant.com/"
          }
        }
      ]
    }
  },
  realEstate: {
    title: 'Inmobiliaria en Madrid | Inversiones Inmobiliarias Espana | SP BD Group',
    titleEn: 'Real Estate in Madrid | Property Investment Spain | SP BD Group',
    description: 'Inversion inmobiliaria en Madrid y Espana. Pisos de lujo, locales comerciales y asesoramiento de inversiones. Expertos en el mercado inmobiliario espanol.',
    descriptionEn: 'Real estate investment in Madrid and Spain. Luxury apartments, commercial properties and investment advisory. Experts in the Spanish property market.',
    keywords: 'inmobiliaria Madrid, inversion inmobiliaria Espana, pisos lujo Madrid, comprar propiedad Espana, real estate Madrid, property investment Spain, luxury apartments Madrid, Spanish property market',
    schema: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      "name": "SP BD Group Inmobiliaria",
      "description": "Servicios inmobiliarios premium en Madrid y Espana",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Madrid",
        "addressRegion": "Madrid",
        "addressCountry": "ES"
      },
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 40.4168,
          "longitude": -3.7038
        },
        "geoRadius": "100000"
      },
      "priceRange": "$$$$"
    }
  },
  finance: {
    title: 'Asesoria Financiera Madrid | Prestamos y Creditos Espana | SP BD Group',
    titleEn: 'Financial Advisory Madrid | Loans & Credit Spain | SP BD Group',
    description: 'Servicios financieros en Madrid: prestamos personales, hipotecas, creditos empresariales y asesoria de inversiones. Tipos de interes competitivos desde 3.5% TAE.',
    descriptionEn: 'Financial services in Madrid: personal loans, mortgages, business credit and investment advisory. Competitive interest rates from 3.5% APR.',
    keywords: 'asesoria financiera Madrid, prestamos personales Espana, hipotecas Madrid, creditos empresariales, financial advisor Madrid, personal loans Spain, mortgages Madrid, business loans Spain, inversion financiera',
    schema: {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "SP BD Group Servicios Financieros",
      "description": "Asesoria financiera y servicios de prestamos en Madrid, Espana",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Madrid",
        "addressRegion": "Madrid",
        "addressCountry": "ES"
      },
      "areaServed": [
        { "@type": "Country", "name": "Spain" },
        { "@type": "AdministrativeArea", "name": "European Union" }
      ],
      "serviceType": ["Prestamos Personales", "Hipotecas", "Creditos Empresariales", "Asesoria de Inversiones"],
      "priceRange": "$$",
      "currenciesAccepted": "EUR",
      "openingHours": "Mo-Fr 09:00-18:00"
    }
  },
  advocacy: {
    title: 'Abogados en Madrid | Servicios Juridicos Espana | SP BD Group',
    titleEn: 'Lawyers in Madrid | Legal Services Spain | SP BD Group',
    description: 'Bufete de abogados en Madrid especializado en derecho mercantil, derecho civil, extranjeria e inmobiliario. Mas de 15 anos de experiencia en Espana.',
    descriptionEn: 'Law firm in Madrid specializing in commercial law, civil law, immigration and real estate. Over 15 years of experience in Spain.',
    keywords: 'abogados Madrid, bufete abogados Espana, derecho mercantil Madrid, abogado extranjeria, lawyers Madrid, law firm Spain, commercial lawyers Madrid, immigration lawyer Spain, legal services Madrid',
    schema: {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "name": "SP BD Group Servicios Juridicos",
      "description": "Bufete de abogados especializado en derecho mercantil, civil y extranjeria en Madrid",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Madrid",
        "addressRegion": "Madrid",
        "addressCountry": "ES"
      },
      "areaServed": [
        { "@type": "Country", "name": "Spain" },
        { "@type": "AdministrativeArea", "name": "European Union" }
      ],
      "knowsAbout": ["Derecho Mercantil", "Derecho Civil", "Extranjeria", "Derecho Inmobiliario"],
      "priceRange": "$$$",
      "openingHours": "Mo-Fr 09:00-19:00"
    }
  },
  importExport: {
    title: 'Importacion Exportacion Espana | Comercio Internacional Europa | SP BD Group',
    titleEn: 'Import Export Spain | International Trade Europe | SP BD Group',
    description: 'Servicios de importacion y exportacion desde Espana a Europa y el mundo. Despacho de aduanas, logistica internacional y asesoria de comercio exterior.',
    descriptionEn: 'Import and export services from Spain to Europe and worldwide. Customs clearance, international logistics and foreign trade advisory.',
    keywords: 'importacion exportacion Espana, comercio internacional Madrid, despacho aduanas, logistica internacional, import export Spain, international trade Madrid, customs clearance Spain, freight forwarding Europe, comercio exterior',
    schema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "SP BD Group Import-Export",
      "description": "Servicios de comercio internacional y logistica desde Espana",
      "@id": "https://spbdgroup.com/import-export",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Madrid",
        "addressRegion": "Madrid",
        "addressCountry": "ES"
      },
      "areaServed": [
        { "@type": "Country", "name": "Spain" },
        { "@type": "Country", "name": "Portugal" },
        { "@type": "Country", "name": "France" },
        { "@type": "Country", "name": "Germany" },
        { "@type": "Country", "name": "Italy" },
        { "@type": "Country", "name": "United Kingdom" },
        { "@type": "Continent", "name": "Europe" },
        { "@type": "Continent", "name": "Asia" },
        { "@type": "Continent", "name": "Americas" }
      ],
      "serviceType": ["Importacion", "Exportacion", "Despacho de Aduanas", "Logistica Internacional"],
      "priceRange": "$$"
    }
  },
  blog: {
    title: 'Blog de Negocios | Noticias Empresariales Espana | SP BD Group',
    titleEn: 'Business Blog | Business News Spain | SP BD Group',
    description: 'Ultimas noticias y consejos sobre negocios en Espana: restaurantes, inmobiliaria, finanzas, comercio internacional y servicios juridicos.',
    descriptionEn: 'Latest news and tips about business in Spain: restaurants, real estate, finance, international trade and legal services.',
    keywords: 'blog negocios Espana, noticias empresariales Madrid, consejos empresas, business blog Spain, company news Madrid',
    schema: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "SP BD Group Blog",
      "description": "Noticias y consejos empresariales de SP BD Group",
      "inLanguage": ["es", "en"]
    }
  },
  contact: {
    title: 'Contacto | SP BD Group Madrid | Telefono y WhatsApp',
    titleEn: 'Contact | SP BD Group Madrid | Phone and WhatsApp',
    description: 'Contacta con SP BD Group en Madrid. Telefono: +34 663 091 990. WhatsApp disponible. Oficinas en Madrid, Espana. Atencion en espanol e ingles.',
    descriptionEn: 'Contact SP BD Group in Madrid. Phone: +34 663 091 990. WhatsApp available. Offices in Madrid, Spain. Service in Spanish and English.',
    keywords: 'contacto SP BD Group, telefono SP BD Group, WhatsApp SP BD Group, oficina Madrid, contact SP BD Group Madrid',
    schema: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contacto SP BD Group",
      "description": "Contacta con SP BD Group en Madrid",
      "mainEntity": {
        "@type": "Organization",
        "name": "SP BD Group",
        "telephone": "+34-663-091-990",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Madrid",
          "addressCountry": "ES"
        }
      }
    }
  },
  services: {
    title: 'Servicios y Precios | SP BD Group Madrid | Soluciones Empresariales',
    titleEn: 'Services & Pricing | SP BD Group Madrid | Business Solutions',
    description: 'Descubre los servicios de SP BD Group: restaurantes, inmobiliaria, finanzas, servicios juridicos e importacion-exportacion. Precios competitivos en Madrid.',
    descriptionEn: 'Discover SP BD Group services: restaurants, real estate, finance, legal services and import-export. Competitive pricing in Madrid.',
    keywords: 'servicios SP BD Group, precios Madrid, soluciones empresariales Espana, services SP BD Group Madrid',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Servicios SP BD Group",
      "provider": {
        "@type": "Organization",
        "name": "SP BD Group",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Madrid",
          "addressCountry": "ES"
        }
      },
      "areaServed": {
        "@type": "Country",
        "name": "Spain"
      }
    }
  },
  login: {
    title: 'Iniciar Sesion | SP BD Group',
    description: 'Inicia sesion en tu cuenta de SP BD Group para gestionar tu perfil y acceder a servicios exclusivos.',
    keywords: 'login SP BD Group, iniciar sesion, acceso cuenta',
    noindex: true
  }
};

// SEO Component for dynamic meta tags with Spain/Europe targeting
export default function SEO({ 
  page = 'home', 
  title, 
  description, 
  keywords,
  image,
  url,
  article = false,
  noindex = false,
  lang = 'es'
}) {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  
  const seoTitle = title || config.title;
  const seoDescription = description || config.description;
  const seoKeywords = keywords || config.keywords;
  const seoImage = image || DEFAULT_IMAGE;
  const seoUrl = url || SITE_URL;
  const shouldNoindex = noindex || config.noindex;

  return (
    <Helmet>
      {/* Language and Region */}
      <html lang={lang} />
      <meta name="language" content="Spanish, English" />
      <meta name="geo.region" content="ES" />
      <meta name="geo.placename" content="Madrid" />
      <meta name="geo.position" content="40.4168;-3.7038" />
      <meta name="ICBM" content="40.4168, -3.7038" />
      
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      
      {/* Robots */}
      {shouldNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />
      
      {/* Hreflang for Spain and International */}
      <link rel="alternate" hrefLang="es-ES" href={seoUrl} />
      <link rel="alternate" hrefLang="es" href={seoUrl} />
      <link rel="alternate" hrefLang="en" href={seoUrl} />
      <link rel="alternate" hrefLang="x-default" href={seoUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:locale:alternate" content="en_GB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Schema.org structured data */}
      {config.schema && (
        <script type="application/ld+json">
          {JSON.stringify(config.schema)}
        </script>
      )}
    </Helmet>
  );
}

// Helper function to generate blog post SEO with Spain targeting
export function BlogPostSEO({ post }) {
  if (!post) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.cover_image,
    "author": {
      "@type": "Person",
      "name": post.author || "SP BD Group"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SP BD Group",
      "logo": {
        "@type": "ImageObject",
        "url": DEFAULT_IMAGE
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Madrid",
        "addressCountry": "ES"
      }
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "inLanguage": "es"
  };

  return (
    <Helmet>
      <html lang="es" />
      <title>{`${post.title} | SP BD Group Blog`}</title>
      <meta name="description" content={post.excerpt} />
      <meta name="keywords" content={`${post.tags?.join(', ') || ''}, SP BD Group, blog negocios Espana`} />
      <meta name="geo.region" content="ES" />
      
      <meta property="og:type" content="article" />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.excerpt} />
      <meta property="og:image" content={post.cover_image} />
      <meta property="og:locale" content="es_ES" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.excerpt} />
      <meta name="twitter:image" content={post.cover_image} />
      
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Breadcrumb schema helper with Spanish labels
export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Local Business schema for specific pages
export function LocalBusinessSchema({ 
  name, 
  description, 
  address = "Madrid, Spain",
  phone = "+34-663-091-990",
  type = "LocalBusiness"
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    "name": name,
    "description": description,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Madrid",
      "addressRegion": "Madrid",
      "addressCountry": "ES"
    },
    "telephone": phone,
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "Spain"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
